import { getApiBaseUrl } from '../../auth/api';
import { authFetch } from '../../auth/authFetch';
import type { ApiResponse, PayRunDetailDto, PayRunFieldError } from './payRunsApiTypes';
import type {
  PayRunPopulationCheckResult,
  PayRunPopulationIssuePage,
  PayRunPopulationPage,
  PayRunPopulationRefreshAccepted,
  PayRunPopulationStatus,
  PayRunTransactionLoadResult,
  PayRunTransactionIssuePage,
  PayRunExceptionsPage,
  PayRunExceptionItem,
  PopulationIssueSeverity,
} from './payRunPopulationApiTypes';

export class PayRunPopulationRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fieldErrors: PayRunFieldError[],
  ) {
    super(message);
    this.name = 'PayRunPopulationRequestError';
  }
}

const base = () => `${getApiBaseUrl()}/api/payrun`;

const parse = async <T>(response: Response): Promise<T> => {
  let body: ApiResponse<T>;
  try {
    body = await response.json() as ApiResponse<T>;
  } catch {
    throw new PayRunPopulationRequestError(
      response.status,
      'payrun_internal_error',
      `Request failed: ${response.status}`,
      [],
    );
  }

  if (!response.ok || !body.success || body.result == null) {
    throw new PayRunPopulationRequestError(
      response.status,
      body.error?.code ?? 'payrun_internal_error',
      body.message || `Request failed: ${response.status}`,
      body.error?.fieldErrors ?? [],
    );
  }
  return body.result;
};

const populationParams = (
  page: number,
  pageSize: number,
  revisionNumber?: number | null,
) => {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (revisionNumber != null) params.set('revisionNumber', String(revisionNumber));
  return params;
};

export const getPayRunDetail = async (
  payId: string,
  signal?: AbortSignal,
): Promise<PayRunDetailDto> =>
  parse<PayRunDetailDto>(await authFetch(`${base()}/${encodeURIComponent(payId)}`, { signal }));

export const getPopulationStatus = async (
  payId: string,
  signal?: AbortSignal,
): Promise<PayRunPopulationStatus> =>
  parse<PayRunPopulationStatus>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/population/status`,
    { signal },
  ));

export const actionPayRunComplianceFinding = async (
  payId: string,
  findingId: string,
): Promise<PayRunExceptionItem> =>
  parse<PayRunExceptionItem>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/compliance-findings/${encodeURIComponent(findingId)}/actions`,
    { method: 'POST' },
  ));

export const getPopulation = async (
  payId: string,
  query: { page: number; pageSize: number; search?: string; revisionNumber?: number | null },
  signal?: AbortSignal,
): Promise<PayRunPopulationPage> => {
  const params = populationParams(query.page, query.pageSize, query.revisionNumber);
  if (query.search?.trim()) params.set('search', query.search.trim());
  return parse<PayRunPopulationPage>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/population?${params}`,
    { signal },
  ));
};

export const getPopulationIssues = async (
  payId: string,
  query: {
    page: number;
    pageSize: number;
    severity?: PopulationIssueSeverity;
    revisionNumber?: number | null;
  },
  signal?: AbortSignal,
): Promise<PayRunPopulationIssuePage> => {
  const params = populationParams(query.page, query.pageSize, query.revisionNumber);
  if (query.severity) params.set('severity', query.severity);
  return parse<PayRunPopulationIssuePage>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/population/issues?${params}`,
    { signal },
  ));
};

export const checkPopulationChanges = async (
  payId: string,
  signal?: AbortSignal,
): Promise<PayRunPopulationCheckResult> =>
  parse<PayRunPopulationCheckResult>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/population/check`,
    { method: 'POST', signal },
  ));

export const refreshPopulation = async (
  payId: string,
): Promise<PayRunPopulationRefreshAccepted> =>
  parse<PayRunPopulationRefreshAccepted>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/population/refresh`,
    { method: 'POST' },
  ));

export type EmployeePopulationSource = 'hr_api' | 'json_upload';

export const loadPopulation = async (
  payId: string,
  request: { employeeSource: EmployeePopulationSource; employeeFile?: File; clearExistingData: boolean },
): Promise<PayRunPopulationRefreshAccepted> => {
  const form = new FormData();
  form.append('employeeSource', request.employeeSource);
  form.append('clearExistingData', String(request.clearExistingData));
  if (request.employeeSource === 'json_upload' && request.employeeFile) {
    form.append('employeeFile', request.employeeFile);
  }
  return parse<PayRunPopulationRefreshAccepted>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/population/load`,
    { method: 'POST', body: form },
  ));
};

export type TransactionSource = 'wfm_api' | 'json_upload';

export const getTransactionStatus = async (
  payId: string,
  signal?: AbortSignal,
): Promise<PayRunTransactionLoadResult> =>
  parse<PayRunTransactionLoadResult>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/transactions/status`,
    { signal },
  ));

export const getTransactionIssues = async (
  payId: string,
  query: { page: number; pageSize: number; severity?: string; revisionNumber?: number | null },
  signal?: AbortSignal,
): Promise<PayRunTransactionIssuePage> => {
  const params = populationParams(query.page, query.pageSize, query.revisionNumber);
  if (query.severity) params.set('severity', query.severity);
  return parse<PayRunTransactionIssuePage>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/transactions/issues?${params}`,
    { signal },
  ));
};

export const getPayRunExceptions = async (
  payId: string,
  query: { page: number; pageSize: number; origin?: string; severity?: string; lifecycle?: string; workEntityId?: string },
  signal?: AbortSignal,
): Promise<PayRunExceptionsPage> => {
  const params = new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) });
  if (query.origin) params.set('origin', query.origin);
  if (query.severity) params.set('severity', query.severity);
  if (query.lifecycle) params.set('lifecycle', query.lifecycle);
  if (query.workEntityId) params.set('workEntityId', query.workEntityId);
  return parse<PayRunExceptionsPage>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/exceptions?${params}`,
    { signal },
  ));
};

export const evaluatePayRunCompliance = async (
  payId: string,
  signal?: AbortSignal,
): Promise<unknown> =>
  parse<unknown>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/compliance/evaluations`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal,
    },
  ));

export const loadTransactions = async (
  payId: string,
  request: { transactionSource: TransactionSource; timesheetFile?: File },
): Promise<PayRunTransactionLoadResult> => {
  const form = new FormData();
  form.append('transactionSource', request.transactionSource);
  if (request.transactionSource === 'json_upload' && request.timesheetFile) {
    form.append('timesheetFile', request.timesheetFile);
  }
  const response = await authFetch(
    `${base()}/${encodeURIComponent(payId)}/transactions/load`,
    { method: 'POST', body: form },
  );
  let body: ApiResponse<PayRunTransactionLoadResult>;
  try {
    body = await response.json() as ApiResponse<PayRunTransactionLoadResult>;
  } catch {
    throw new PayRunPopulationRequestError(response.status, 'payrun_internal_error', `Request failed: ${response.status}`, []);
  }
  if (body.result != null && typeof body.result.status === 'string' && Array.isArray(body.result.errors)) {
    return body.result;
  }
  throw new PayRunPopulationRequestError(
    response.status,
    body.error?.code ?? 'payrun_internal_error',
    body.message || `Request failed: ${response.status}`,
    body.error?.fieldErrors ?? [],
  );
};
